import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Flag, X as XIcon } from 'lucide-react';
import { reportarDato } from '@lib/services/portal.service';
import { toDDMMYYYY, toISO } from '@lib/bajas-revision';

/**
 * Botón "Reportar dato incorrecto" al lado de cada campo bloqueado del
 * wizard. Al click abre un popover inline donde el titular indica el
 * VALOR CORRECTO del campo — sin texto libre genérico, para que la
 * observación que recibe RRHH/Medicus sea accionable y no anecdótica.
 *
 * Variantes según `tipo`:
 *  - `text`     → input single-line (nombres, apellido, documento, cuil, etc.)
 *  - `date`     → input type=date (fechaNacimiento)
 *  - `numeric`  → input single-line con inputMode=numeric (cuil sin guiones, dni, etc.)
 *  - `enum`     → <select> con `opciones`. El titular elige una opción.
 *
 * El comentario libre (textarea) sólo se renderiza cuando `permiteComentario=true`
 * y queda opcional (max 200 chars). Hoy el único campo que lo activa es `sexo`,
 * para que el titular pueda aclarar identidades no contempladas en M/F.
 *
 * Si el dato ya fue reportado (`yaReportado=true`), muestra un badge en
 * lugar del botón para evitar reportes duplicados.
 */
export type ReportarCampoTipo = 'text' | 'date' | 'numeric' | 'enum';

interface OpcionEnum {
  value: string;
  label: string;
}

interface Props {
  token: string;
  asociadoId: string;
  campo: string;
  labelCampo: string;
  valorActual?: string;
  yaReportado: boolean;
  tipo: ReportarCampoTipo;
  /** Sólo para tipo='enum'. */
  opciones?: OpcionEnum[];
  /** Si true, renderiza un textarea opcional para comentario (max 200 chars). */
  permiteComentario?: boolean;
}

const MAX_VALOR = 200;
const MAX_COMENTARIO = 200;

export function ReportarCampo({
  token,
  asociadoId,
  campo,
  labelCampo,
  valorActual,
  yaReportado,
  tipo,
  opciones,
  permiteComentario = false,
}: Props) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [valor, setValor] = useState('');
  const [comentario, setComentario] = useState('');
  const [errUI, setErrUI] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      reportarDato(token, {
        asociadoId,
        campo,
        valorPropuesto: valor.trim(),
        mensaje: permiteComentario && comentario.trim() ? comentario.trim() : undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['onboarding', token] });
      cerrar();
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: { message?: string } } } };
      setErrUI(err.response?.data?.error?.message ?? 'No se pudo enviar el reporte.');
    },
  });

  function cerrar() {
    setOpen(false);
    setValor('');
    setComentario('');
    setErrUI(null);
  }

  if (yaReportado) {
    return (
      <span
        title={`Ya reportaste ${labelCampo} como incorrecto`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          fontSize: 11,
          color: '#B45309',
          background: '#FEF3C7',
          padding: '2px 8px',
          borderRadius: 12,
          fontWeight: 500,
        }}
      >
        <Flag size={11} /> Reportado
      </span>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          fontSize: 11,
          color: 'var(--color-primary-darker)',
          background: 'transparent',
          border: '1px dashed var(--color-outline)',
          padding: '2px 8px',
          borderRadius: 12,
          cursor: 'pointer',
        }}
      >
        <Flag size={11} /> Reportar como incorrecto
      </button>
    );
  }

  const valorValido = tipo === 'enum' ? !!valor : valor.trim().length > 0;

  return (
    <div
      role="dialog"
      aria-label={`Reportar ${labelCampo} como incorrecto`}
      style={{
        marginTop: 6,
        padding: 10,
        borderRadius: 10,
        background: '#FEF3C7',
        border: '1px solid #FBBF24',
      }}
    >
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}
      >
        <strong style={{ fontSize: 12, color: '#7C2D12' }}>
          ¿Cuál es el valor correcto de {labelCampo}?
        </strong>
        <button
          type="button"
          onClick={cerrar}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: '#7C2D12',
            display: 'inline-flex',
          }}
          aria-label="Cancelar"
        >
          <XIcon size={14} />
        </button>
      </div>

      {valorActual && (
        <p style={{ fontSize: 11, color: '#7C2D12', margin: '4px 0', opacity: 0.7 }}>
          Valor actual: <code>{valorActual}</code>
        </p>
      )}

      <ReportarCampoInput
        tipo={tipo}
        opciones={opciones}
        valor={valor}
        onChange={setValor}
      />

      {permiteComentario && (
        <textarea
          className="input"
          rows={2}
          placeholder="Comentario opcional (max 200 chars)"
          value={comentario}
          onChange={(e) => setComentario(e.target.value)}
          maxLength={MAX_COMENTARIO}
          style={{ marginTop: 6, fontSize: 13, width: '100%' }}
        />
      )}

      {errUI && (
        <p style={{ fontSize: 11, color: '#7F1D1D', marginTop: 4 }}>{errUI}</p>
      )}

      <div style={{ display: 'flex', gap: 6, marginTop: 6, justifyContent: 'flex-end' }}>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={cerrar}
          style={{ padding: '4px 10px', fontSize: 12 }}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!valorValido || mut.isPending}
          onClick={() => mut.mutate()}
          style={{ padding: '4px 10px', fontSize: 12 }}
        >
          <span className="btn-font-style">
            {mut.isPending ? 'Enviando…' : 'Reportar'}
          </span>
        </button>
      </div>
    </div>
  );
}

/**
 * Render del input específico según tipo. Separado del componente padre para
 * que `ReportarCampo` solo orqueste estado y mutation; este componente sólo
 * pinta el control de entrada apropiado.
 */
function ReportarCampoInput({
  tipo,
  opciones,
  valor,
  onChange,
}: {
  tipo: ReportarCampoTipo;
  opciones?: OpcionEnum[];
  valor: string;
  onChange: (v: string) => void;
}) {
  if (tipo === 'enum') {
    return (
      <select
        className="input"
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        style={{ marginTop: 4, fontSize: 13, width: '100%' }}
      >
        <option value="">Elegí una opción…</option>
        {(opciones ?? []).map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }

  if (tipo === 'date') {
    // El input nativo trabaja en aaaa-mm-dd; el portal guarda dd/mm/aaaa
    // (VEN-565). Se muestra convertido y se reporta en dd/mm/aaaa: si viajaba
    // en ISO, al aceptar la observación la fecha quedaba así en la ficha.
    return (
      <input
        className="input"
        type="date"
        value={toISO(valor)}
        onChange={(e) => onChange(toDDMMYYYY(e.target.value))}
        maxLength={MAX_VALOR}
        style={{ marginTop: 4, fontSize: 13, width: '100%' }}
      />
    );
  }

  const inputMode = tipo === 'numeric' ? 'numeric' : 'text';
  return (
    <input
      className="input"
      type="text"
      inputMode={inputMode}
      value={valor}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Ingresá el valor correcto"
      maxLength={MAX_VALOR}
      style={{ marginTop: 4, fontSize: 13, width: '100%' }}
    />
  );
}
